import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Button,
    CircularProgress,
    Alert,
    TextField,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    FormControlLabel,
    Grid,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useOrganization } from '../contexts/OrganizationContext';
import { useOrganizationsListPaginated } from '../api/hooks/useOrganizations';
import { useTablePagination, useSearchWithPagination } from '../api/hooks/useTablePagination';
import { fetchOrganizationsListPaginated, OrganizationListDto } from '../api/services/organization.service';
import { generateMapImage } from '../utils/mapImageGenerator';
import { api } from '../api/client/axiosClient';
import { ENDPOINTS } from '../api/endpoints';
import { useSnackbar } from 'notistack';

interface LogoGenerationResult {
    organizationId: string;
    organizationName: string;
    success: boolean;
    error?: string;
}

// Define step names for the Stepper
const steps = [
    'Seleccionar Organizaciones',
    'Generar Logos',
    'Resultados',
];

const LogosFromMapPage: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [selectedOrgs, setSelectedOrgs] = useState<OrganizationListDto[]>([]);
    const [selectingAllMatching, setSelectingAllMatching] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [currentProcessingIndex, setCurrentProcessingIndex] = useState(0);
    const [results, setResults] = useState<LogoGenerationResult[]>([]);
    const [processingError, setProcessingError] = useState<string | null>(null);

    const { selectedOrganization } = useOrganization();
    const { enqueueSnackbar } = useSnackbar();

    // Pagination state
    const {
        page,
        pageSize,
        handlePageChange,
        handlePageSizeChange,
        resetPagination,
    } = useTablePagination({
        initialPage: 0,
        initialPageSize: 25,
    });

    // Search state with debouncing
    const {
        searchTerm,
        debouncedSearchTerm,
        setSearchTerm,
        clearSearch,
    } = useSearchWithPagination(1000, resetPagination);

    // Use paginated hook with search
    const {
        data: orgListData,
        isLoading: orgListLoading,
    } = useOrganizationsListPaginated(
        selectedOrganization?.id || 'placeholder',
        {
            page,
            size: pageSize,
            searchTerm: debouncedSearchTerm || undefined,
        },
        {
            enabled: !!selectedOrganization?.id
        }
    );

    // Get current page organizations
    const currentPageOrgs = useMemo(() => {
        if (!orgListData || !orgListData.data) return [];
        const content = orgListData.data.content;
        return Array.isArray(content) ? content : [];
    }, [orgListData]);

    // Check if all current page orgs are selected
    const allCurrentPageSelected = useMemo(() => {
        if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
        return currentPageOrgs.every(org => selectedOrgs.some(sel => sel.id === org.id));
    }, [currentPageOrgs, selectedOrgs]);

    // Check if some (but not all) current page orgs are selected
    const someCurrentPageSelected = useMemo(() => {
        if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
        return currentPageOrgs.some(org => selectedOrgs.some(sel => sel.id === org.id)) && !allCurrentPageSelected;
    }, [currentPageOrgs, selectedOrgs, allCurrentPageSelected]);

    // Handle select all on current page
    const handleSelectAllCurrentPage = (checked: boolean) => {
        if (!Array.isArray(currentPageOrgs)) return;

        if (checked) {
            const newSelected = [...selectedOrgs];
            currentPageOrgs.forEach(org => {
                if (!newSelected.some(sel => sel.id === org.id)) {
                    newSelected.push(org);
                }
            });
            setSelectedOrgs(newSelected);
        } else {
            setSelectedOrgs(selectedOrgs.filter(sel => !currentPageOrgs.some(org => org.id === sel.id)));
        }
    };

    // Handle select all matching filter (all pages)
    const handleSelectAllMatchingFilter = useCallback(async () => {
        if (!selectedOrganization?.id) return;

        setSelectingAllMatching(true);
        try {
            const allOrgs: OrganizationListDto[] = [];
            let currentPage = 0;
            let hasMore = true;
            const pageSizeForFetch = 100;

            while (hasMore) {
                const response: any = await fetchOrganizationsListPaginated(
                    selectedOrganization.id,
                    {
                        page: currentPage,
                        size: pageSizeForFetch,
                        searchTerm: debouncedSearchTerm || undefined,
                    }
                );

                if (response.data && response.data.content && Array.isArray(response.data.content) && response.data.content.length > 0) {
                    allOrgs.push(...response.data.content);
                    currentPage++;

                    if (response.data.content.length < pageSizeForFetch || currentPage >= response.data.totalPages) {
                        hasMore = false;
                    }
                } else {
                    hasMore = false;
                }
            }

            setSelectedOrgs(prev => {
                const newSelected = [...prev];
                allOrgs.forEach(org => {
                    if (!newSelected.some(sel => sel.id === org.id)) {
                        newSelected.push(org);
                    }
                });
                return newSelected;
            });
        } catch (error) {
            console.error('Error selecting all matching organizations:', error);
        } finally {
            setSelectingAllMatching(false);
        }
    }, [selectedOrganization?.id, debouncedSearchTerm]);

    const handleToggleOrg = (org: OrganizationListDto) => {
        setSelectedOrgs(prev =>
            prev.some(sel => sel.id === org.id)
                ? prev.filter(sel => sel.id !== org.id)
                : [...prev, org]
        );
    };

    const handleRemoveOrg = (orgId: string) => {
        setSelectedOrgs(prev => prev.filter(sel => sel.id !== orgId));
    };

    // Fetch organization details to get coordinates
    const fetchOrganizationDetails = async (orgId: string) => {
        try {
            const response: any = await api.get(ENDPOINTS.organizations.details(orgId));
            return response.data;
        } catch (error) {
            console.error('Error fetching organization details:', error);
            return null;
        }
    };

    // Convert data URL to Blob
    const dataURLtoBlob = (dataURL: string): Blob => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    };

    // Process logos generation
    const handleProcessLogos = async () => {
        if (selectedOrgs.length === 0) {
            setProcessingError('No hay organizaciones seleccionadas.');
            return;
        }

        setProcessing(true);
        setProcessingError(null);
        setResults([]);
        setCurrentProcessingIndex(0);
        setActiveStep(1);

        const processResults: LogoGenerationResult[] = [];

        // Process organizations sequentially (one by one)
        for (let i = 0; i < selectedOrgs.length; i++) {
            const org = selectedOrgs[i];
            setCurrentProcessingIndex(i);

            try {
                // Fetch organization details to get coordinates
                const details = await fetchOrganizationDetails(org.id);

                if (!details) {
                    processResults.push({
                        organizationId: org.id,
                        organizationName: org.name,
                        success: false,
                        error: 'No se pudieron obtener los detalles de la organización'
                    });
                    continue;
                }

                const farmLocation = details.farmLocations?.[0];

                if (!farmLocation || !farmLocation.latitude || !farmLocation.longitude) {
                    processResults.push({
                        organizationId: org.id,
                        organizationName: org.name,
                        success: false,
                        error: 'La organización no tiene coordenadas registradas'
                    });
                    continue;
                }

                // Generate map image
                const mapImageDataUrl = await generateMapImage(
                    farmLocation.latitude,
                    farmLocation.longitude,
                    farmLocation.area
                );

                if (!mapImageDataUrl) {
                    processResults.push({
                        organizationId: org.id,
                        organizationName: org.name,
                        success: false,
                        error: 'No se pudo generar la imagen del mapa'
                    });
                    continue;
                }

                // Convert data URL to Blob and then to File
                const blob = dataURLtoBlob(mapImageDataUrl);
                const file = new File([blob], `map-logo-${org.id}.png`, { type: 'image/png' });

                // Create FormData for the update
                const formData = new FormData();
                formData.append('organization', JSON.stringify({}));
                formData.append('logo', file);

                // Call the update endpoint
                await api.put(`${ENDPOINTS.organizations.base()}/${org.id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                processResults.push({
                    organizationId: org.id,
                    organizationName: org.name,
                    success: true
                });

                // Revoke blob URL to free memory
                URL.revokeObjectURL(mapImageDataUrl);

            } catch (error: any) {
                processResults.push({
                    organizationId: org.id,
                    organizationName: org.name,
                    success: false,
                    error: error.response?.data?.message || error.message || 'Error desconocido'
                });
            }

            // Small delay between organizations to avoid overwhelming the browser
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        setResults(processResults);
        setActiveStep(2);
        setProcessing(false);

        // Show summary
        const successCount = processResults.filter(r => r.success).length;
        const errorCount = processResults.filter(r => !r.success).length;

        enqueueSnackbar(
            `Procesamiento completado: ${successCount} exitosos, ${errorCount} con errores`,
            { variant: successCount > 0 ? 'success' : 'warning' }
        );
    };

    const handleReset = () => {
        setActiveStep(0);
        setSelectedOrgs([]);
        setResults([]);
        setProcessingError(null);
        clearSearch();
        resetPagination();
    };

    const getStepContent = (step: number) => {
        switch (step) {
            case 0: // Select Organizations
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 500 }}>
                        {/* Search and selection controls */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                                label="Buscar Organización"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                fullWidth
                                placeholder="Nombre, ID, grupo, dueño o teléfono"
                                size="small"
                            />
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allCurrentPageSelected}
                                            indeterminate={someCurrentPageSelected}
                                            onChange={e => handleSelectAllCurrentPage(e.target.checked)}
                                            disabled={orgListLoading || currentPageOrgs.length === 0 || selectingAllMatching}
                                        />
                                    }
                                    label="Seleccionar todos los de esta página"
                                    sx={{ whiteSpace: 'nowrap' }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={false}
                                            onChange={() => handleSelectAllMatchingFilter()}
                                            disabled={orgListLoading || selectingAllMatching}
                                        />
                                    }
                                    label={
                                        selectingAllMatching ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} />
                                                <span>Seleccionando...</span>
                                            </Box>
                                        ) : (
                                            'Seleccionar todos los de este filtro'
                                        )
                                    }
                                    sx={{ whiteSpace: 'nowrap' }}
                                />
                            </Box>
                        </Box>

                        {/* Two-column layout */}
                        <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 400 }}>
                            {/* Left column: Available organizations */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Organizaciones Disponibles
                                    </Typography>
                                    {orgListLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <>
                                            <List
                                                sx={{
                                                    maxHeight: 350,
                                                    overflow: 'auto',
                                                    border: '1px solid #eee',
                                                    borderRadius: 1,
                                                    flexGrow: 1
                                                }}
                                                disablePadding
                                            >
                                                {currentPageOrgs.map(org => {
                                                    const isSelected = selectedOrgs.some(sel => sel.id === org.id);
                                                    return (
                                                        <ListItem
                                                            key={org.id}
                                                            onClick={() => handleToggleOrg(org)}
                                                            button
                                                            selected={isSelected}
                                                            {...({} as any)}
                                                        >
                                                            <ListItemIcon>
                                                                <Checkbox
                                                                    edge="start"
                                                                    checked={isSelected}
                                                                    tabIndex={-1}
                                                                    disableRipple
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={`${org.name} (${org.idNumber})`}
                                                                secondary={`Dueño: ${org.owner?.name || 'N/A'} | Tel: ${org.owner?.phones?.join(', ') || 'N/A'}`}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                                {currentPageOrgs.length === 0 && !orgListLoading && (
                                                    <ListItem>
                                                        <ListItemText primary="No se encontraron organizaciones con los filtros actuales." />
                                                    </ListItem>
                                                )}
                                            </List>

                                            {/* Pagination controls */}
                                            {orgListData && orgListData.data && orgListData.data.totalPages > 0 && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                                            <InputLabel>Por página</InputLabel>
                                                            <Select
                                                                value={pageSize}
                                                                onChange={(e) => handlePageSizeChange(e.target.value as number)}
                                                                label="Por página"
                                                            >
                                                                <MenuItem value={25}>25</MenuItem>
                                                                <MenuItem value={50}>50</MenuItem>
                                                                <MenuItem value={100}>100</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {orgListData.data.totalElements} registros
                                                        </Typography>
                                                    </Box>
                                                    {orgListData.data.totalPages > 1 && (
                                                        <Pagination
                                                            count={orgListData.data.totalPages}
                                                            page={orgListData.data.page + 1}
                                                            onChange={(_event, value) => handlePageChange(value - 1)}
                                                            color="primary"
                                                            size="small"
                                                            showFirstButton
                                                            showLastButton
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Grid>

                            {/* Right column: Selected organizations */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                        Organizaciones Seleccionadas ({selectedOrgs.length})
                                    </Typography>
                                    <List
                                        sx={{
                                            maxHeight: 350,
                                            overflow: 'auto',
                                            border: '1px solid #eee',
                                            borderRadius: 1,
                                            flexGrow: 1
                                        }}
                                        disablePadding
                                    >
                                        {selectedOrgs.map(org => (
                                            <ListItem
                                                key={org.id}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="Eliminar"
                                                        onClick={() => handleRemoveOrg(org.id)}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={`${org.name} (${org.idNumber})`}
                                                    secondary={`Dueño: ${org.owner?.name || 'N/A'}`}
                                                />
                                            </ListItem>
                                        ))}
                                        {selectedOrgs.length === 0 && (
                                            <ListItem>
                                                <ListItemText primary="No hay organizaciones seleccionadas." />
                                            </ListItem>
                                        )}
                                    </List>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                );

            case 1: // Processing
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6">
                            Generando logos desde mapas...
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Procesando {currentProcessingIndex + 1} de {selectedOrgs.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {selectedOrgs[currentProcessingIndex]?.name || ''}
                        </Typography>
                        <Box sx={{ width: '100%', maxWidth: 400 }}>
                            <LinearProgress
                                variant="determinate"
                                value={(currentProcessingIndex / selectedOrgs.length) * 100}
                            />
                        </Box>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Por favor no cierre esta página mientras se procesan los logos.
                            El proceso puede tomar varios minutos dependiendo de la cantidad de organizaciones.
                        </Alert>
                    </Box>
                );

            case 2: { // Results
                const successCount = results.filter(r => r.success).length;
                const errorCount = results.filter(r => !r.success).length;
                const successRate = results.length > 0 ? ((successCount / results.length) * 100).toFixed(1) : 0;

                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Reporte de Procesamiento
                        </Typography>

                        {/* Statistics */}
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            mb: 3,
                            p: 3,
                            backgroundColor: '#f8f9fa',
                            borderRadius: 2,
                            border: '1px solid #e9ecef'
                        }}>
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography variant="h3" color="primary">
                                    {results.length}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Total Procesados
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography variant="h3" color="success.main">
                                    {successCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Exitosos
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography variant="h3" color="error.main">
                                    {errorCount}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Con Errores
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', flex: 1 }}>
                                <Typography variant="h3" color={Number(successRate) >= 80 ? 'success.main' : Number(successRate) >= 60 ? 'warning.main' : 'error.main'}>
                                    {successRate}%
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Tasa de Éxito
                                </Typography>
                            </Box>
                        </Box>

                        {/* Error summary */}
                        {errorCount > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Errores Encontrados:
                                </Typography>
                                <Box sx={{
                                    maxHeight: 200,
                                    overflow: 'auto',
                                    p: 2,
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: 1
                                }}>
                                    {results.filter(r => !r.success).map(result => (
                                        <Typography key={result.organizationId} variant="body2" sx={{ mb: 0.5 }}>
                                            • <strong>{result.organizationName}</strong>: {result.error}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Download report button */}
                        <Box sx={{ textAlign: 'center', mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    const csvContent = [
                                        'Organization ID,Nombre,Estado,Error',
                                        ...results.map(r => [
                                            r.organizationId,
                                            r.organizationName,
                                            r.success ? 'Exitoso' : 'Error',
                                            r.error || ''
                                        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
                                    ].join('\n');

                                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                    const url = URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.setAttribute('href', url);
                                    link.setAttribute('download', `logos-from-map-report-${new Date().toISOString().split('T')[0]}.csv`);
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                }}
                                sx={{ mr: 2 }}
                            >
                                Descargar Reporte (CSV)
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleReset}
                            >
                                Procesar Más Organizaciones
                            </Button>
                        </Box>
                    </Box>
                );
            }

            default:
                return 'Unknown step';
        }
    };

    return (
        <Container maxWidth="lg">
            <Paper elevation={3} sx={{ my: 4, p: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Logos Desde Mapa
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    Genera logos automáticamente desde las coordenadas del mapa de cada organización
                </Typography>

                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {processingError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{processingError}</Alert>
                )}

                <Box sx={{ mt: 2, mb: 2, p: 2, border: '1px dashed grey', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                    {getStepContent(activeStep)}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                    {activeStep === 0 && (
                        <>
                            <Box sx={{ flex: '1 1 auto' }} />
                            <Button
                                variant="contained"
                                onClick={handleProcessLogos}
                                disabled={processing || selectedOrgs.length === 0 || selectingAllMatching}
                            >
                                Generar Logos ({selectedOrgs.length})
                            </Button>
                        </>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default LogosFromMapPage;
